import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

/**
 * Audit-friendly investment breakdown panel.
 *
 *   - Lists every investment line that contributes to this part's lifetime
 *     amortisation: own + every descendant in the BOM tree, with its source
 *     (outline + part name) and category.
 *   - Stacked bar by category × source so the user instantly sees BOTH which
 *     type of investment (Tooling vs Fixture vs Gauge vs Machine vs Other)
 *     AND where in the BOM it sits (own / each child).
 *   - "Compact" mode (used inside the Proposal sheet) renders only the table.
 */
const COLORS_SOURCE = ["#002FA7", "#FFB703", "#9C27B0", "#43A047", "#E63946", "#0EA5E9", "#F97316", "#6366F1", "#84CC16", "#EC4899"];

const rs = (x) => `₹ ${Number(x || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function InvestmentBreakdownPanel({ partId, compact = false, testIdPrefix = "investment-breakdown" }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!partId) return;
        let cancel = false;
        setData(null);
        setErr("");
        api.get(`/parts/${partId}/investment-breakdown`)
            .then((r) => { if (!cancel) setData(r.data); })
            .catch((e) => { if (!cancel) setErr(e?.response?.data?.detail || "Couldn't load investment breakdown"); });
        return () => { cancel = true; };
    }, [partId]);

    // Build a [category × source] stacked-bar dataset.
    const { stacked, sources } = useMemo(() => {
        if (!data?.rows?.length) return { stacked: [], sources: [] };
        const sourcesMap = {};
        data.rows.forEach((r) => {
            const key = `${r.source_outline} ${r.source_part_name}`;
            sourcesMap[key] = (sourcesMap[key] || 0) + r.value;
        });
        const sourcesArr = Object.keys(sourcesMap);
        const byCategory = {};
        data.rows.forEach((r) => {
            const cat = r.category || "Other";
            if (!byCategory[cat]) byCategory[cat] = { category: cat };
            const key = `${r.source_outline} ${r.source_part_name}`;
            byCategory[cat][key] = (byCategory[cat][key] || 0) + r.value;
        });
        return { stacked: Object.values(byCategory), sources: sourcesArr };
    }, [data]);

    if (err) return <div className="text-xs text-[#E63946] italic" data-testid={`${testIdPrefix}-error`}>{err}</div>;
    if (!data) return <div className="text-xs text-zinc-400 italic" data-testid={`${testIdPrefix}-loading`}>Loading investment breakdown…</div>;
    if (!data.rows.length) {
        return <div className="text-xs text-zinc-400 italic" data-testid={`${testIdPrefix}-empty`}>No investment items found across this part and its descendants.</div>;
    }

    return (
        <div className="space-y-3" data-testid={testIdPrefix}>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                {data.rows.length} line{data.rows.length !== 1 ? "s" : ""} · {sources.length} source{sources.length !== 1 ? "s" : ""}
                <span className="ml-3">Total: <span className="font-semibold text-zinc-800">{rs(data.total_value)}</span></span>
                <span className="ml-3">Lifetime vol: <span className="font-semibold text-zinc-800">{(data.lifetime_volume || 0).toLocaleString("en-IN")}</span></span>
                <span className="ml-3">Per-piece: <span className="font-semibold text-zinc-800">{rs(data.total_per_piece)}</span></span>
            </div>

            {!compact && stacked.length > 0 && (
                <div className="bg-white" style={{ height: 220 }} data-testid={`${testIdPrefix}-chart`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stacked} margin={{ top: 8, right: 16, left: 8, bottom: 36 }}>
                            <CartesianGrid stroke="#f0f0f0" />
                            <XAxis dataKey="category" angle={-15} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={56} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(v) => rs(v)} />
                            <Legend wrapperStyle={{ fontSize: 9 }} />
                            {sources.map((src, i) => (
                                <Bar key={src} dataKey={src} stackId="src" name={src} fill={COLORS_SOURCE[i % COLORS_SOURCE.length]} isAnimationActive={false} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            <div className="overflow-x-auto border border-zinc-200">
                <table className="w-full text-xs">
                    <thead className="bg-zinc-50">
                        <tr>
                            <th className="text-left px-2 py-1.5 font-mono uppercase tracking-wider text-[9px] text-zinc-600">Outline</th>
                            <th className="text-left px-2 py-1.5 font-mono uppercase tracking-wider text-[9px] text-zinc-600">Source Part</th>
                            <th className="text-left px-2 py-1.5 font-mono uppercase tracking-wider text-[9px] text-zinc-600">Investment</th>
                            <th className="text-left px-2 py-1.5 font-mono uppercase tracking-wider text-[9px] text-zinc-600">Category</th>
                            <th className="text-right px-2 py-1.5 font-mono uppercase tracking-wider text-[9px] text-zinc-600">Value (₹)</th>
                            <th className="text-center px-2 py-1.5 font-mono uppercase tracking-wider text-[9px] text-zinc-600">Amortise</th>
                            <th className="text-right px-2 py-1.5 font-mono uppercase tracking-wider text-[9px] text-zinc-600">Per Piece (₹)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((r, i) => (
                            <tr key={`${r.source_part_id}-${r.index}`} className="border-t border-zinc-100" data-testid={`${testIdPrefix}-row-${i}`}>
                                <td className="px-2 py-1.5 font-mono text-[#002FA7] font-semibold">{r.source_outline}</td>
                                <td className="px-2 py-1.5">
                                    {r.source_part_name}
                                    {r.source_part_number && <span className="text-zinc-400 font-mono ml-1">({r.source_part_number})</span>}
                                    {r.is_root && <span className="ml-1 text-[8px] uppercase tracking-wider bg-zinc-200 px-1 py-0.5">own</span>}
                                </td>
                                <td className="px-2 py-1.5">{r.name}</td>
                                <td className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-600">{r.category}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{rs(r.value)}</td>
                                <td className="px-2 py-1.5 text-center text-[10px]">
                                    {r.amortise ? <span className="text-[#43A047]">yes</span> : <span className="text-zinc-400">no</span>}
                                </td>
                                <td className="px-2 py-1.5 text-right font-mono">{r.amortise ? rs(r.per_piece) : <span className="text-zinc-300">—</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-zinc-50 border-t border-zinc-200">
                        <tr>
                            <td colSpan={4} className="px-2 py-1.5 text-right font-mono uppercase tracking-wider text-[10px] text-zinc-600">Total</td>
                            <td className="px-2 py-1.5 text-right font-mono font-bold" data-testid={`${testIdPrefix}-total-value`}>{rs(data.total_value)}</td>
                            <td className="px-2 py-1.5" />
                            <td className="px-2 py-1.5 text-right font-mono font-bold" data-testid={`${testIdPrefix}-total-per-piece`}>{rs(data.total_per_piece)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}
